// @ts-nocheck
/**
 * TrackingScripts — Dispara eventos de pagina do Agendali
 * O pixel base (fbq init + PageView) ja esta hardcoded no index.html
 * Este componente apenas dispara eventos especificos por pagina
 * SO carrega em: agendali.com.br e app.agendali.com.br
 * NUNCA em dominios de clientes
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ALLOWED_HOSTS = ['agendali.com.br', 'app.agendali.com.br', 'www.agendali.com.br', 'localhost'];
const ALLOWED_PATHS = ['/', '/login', '/signup', '/signup-v2', '/onboarding'];
const ALLOWED_PREFIXES = ['/blog'];
// Páginas que carregam o platform-scripts.js (gtag + Meta Pixel "novo",
// específico da conversão da PLATAFORMA — nada a ver com pixel/gtag de
// cada empresa cliente, que é outro mecanismo, isolado por company_id).
const SCRIPT_LOAD_PATHS = ['/signup', '/signup-v2', '/onboarding'];

function isAgendaliHost(): boolean {
  const host = window.location.hostname;
  return ALLOWED_HOSTS.includes(host) || host === 'localhost';
}

function isAgendaliPage(pathname: string): boolean {
  if (!isAgendaliHost()) return false;
  if (ALLOWED_PATHS.includes(pathname)) return true;
  for (const prefix of ALLOWED_PREFIXES) {
    if (pathname.startsWith(prefix)) return true;
  }
  return false;
}

// ── Módulo único de rastreamento do piloto signup-v2 ────────────────────────
// Reaproveitado por SignupV2Page.tsx e OnboardingPage.tsx — nenhuma chamada
// fbq/gtag solta nessas páginas, tudo passa por aqui. Dedup via localStorage
// (sobrevive a reload) chaveado por evento + company_id, pra nunca duplicar
// em reload, "voltar" do navegador ou duplo clique.
function alreadySent(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function markSent(key: string): void {
  try { localStorage.setItem(key, '1'); } catch { /* localStorage indisponível: melhor deixar passar a não travar o fluxo */ }
}

/** Evento interno por etapa do onboarding — via dataLayer (GTM já carregado globalmente). */
export function trackOnboardingStepEvent(step: string, companyId?: number | string): void {
  if (!isAgendaliHost()) return;
  try {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event: 'signup_v2_step', step, company_id: companyId });
  } catch (_) { /* nunca quebra o fluxo do usuário por causa de tracking */ }
}

/**
 * Meta Lead — só após sucesso REAL de criação de conta (chamar 1x, pós-201 do
 * backend). `capiEventId` é o mesmo event_id que o backend usou para disparar
 * a CAPI (POST /auth/register-v2 devolve `capi_event_id`) — passar o mesmo ID
 * pro fbq() do navegador é o que permite o Meta deduplicar os dois sinais
 * (navegador + servidor) em um só evento, em vez de contar duas vezes.
 */
export function trackAccountCreated(companyId: number | string, capiEventId?: string): void {
  if (!isAgendaliHost()) return;
  const key = `sv2_lead_${companyId}`;
  if (alreadySent(key)) return;
  const fbq = (window as any).fbq;
  if (typeof fbq === 'function') {
    const eventID = capiEventId || key;
    fbq('track', 'Lead', { content_name: 'SignupV2AccountCreated', currency: 'BRL', value: 39.90 }, { eventID });
  }
  trackOnboardingStepEvent('account_created', companyId);
  markSent(key);
}

/**
 * Meta CompleteRegistration + conversão principal do Google Ads — só depois
 * que o BACKEND confirma PUBLISHED (chamada após o 200 de /onboarding/publish,
 * nunca antes, nunca de forma otimista). Mesmo esquema de dedup por event_id
 * do trackAccountCreated acima. send_to usa o ID + label de conversão
 * completos confirmados no /signup atual (SignupPage.tsx) — o ID base sozinho,
 * sem o label, não tem destino configurado no gtag e a conversão se perde.
 */
export function trackPublished(companyId: number | string, capiEventId?: string): void {
  if (!isAgendaliHost()) return;
  const key = `sv2_published_${companyId}`;
  if (alreadySent(key)) return;
  const fbq = (window as any).fbq;
  if (typeof fbq === 'function') {
    const eventID = capiEventId || key;
    fbq('track', 'CompleteRegistration', { content_name: 'SignupV2Published', currency: 'BRL', value: 39.90, status: true }, { eventID });
  }
  const gtag = (window as any).gtag;
  if (typeof gtag === 'function') gtag('event', 'conversion', { send_to: 'AW-16596097730/8NlhCISwgqscEMK10ek9', value: 1.0, currency: 'BRL' });
  trackOnboardingStepEvent('published', companyId);
  markSent(key);
}

export default function TrackingScripts() {
  const { pathname } = useLocation();

  // Carrega platform-scripts.js dinamicamente no /signup e no piloto
  // (/signup-v2 + /onboarding), e somente em dominios proprios da Agendali.
  // Quando o script for removido no ADM, o arquivo passa a conter apenas um
  // comentario — nenhum codigo executa.
  useEffect(() => {
    if (!SCRIPT_LOAD_PATHS.includes(pathname)) return;
    if (!isAgendaliHost()) return;
    const id = 'agendali-platform-scripts-signup';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.src = '/platform-scripts.js';
    s.async = true;
    document.head.appendChild(s);
  }, [pathname]);

  useEffect(() => {
    if (!isAgendaliPage(pathname)) return;

    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    // Evento especifico por pagina
    if (pathname === '/signup') {
      fbq('track', 'ViewContent', { content_name: 'Signup' });
    } else if (pathname === '/signup-v2') {
      fbq('track', 'ViewContent', { content_name: 'SignupV2' });
    } else if (pathname === '/onboarding') {
      fbq('track', 'ViewContent', { content_name: 'OnboardingV2' });
    } else if (pathname === '/login') {
      fbq('track', 'ViewContent', { content_name: 'Login' });
    } else if (pathname === '/') {
      fbq('track', 'ViewContent', { content_name: 'Landing' });
    } else if (pathname.startsWith('/blog')) {
      fbq('track', 'ViewContent', { content_name: 'Blog' });
    }
  }, [pathname]);

  return null;
}
